// Analytics tracking utility

interface AnalyticsEvent {
  category: string;
  action: string;
  label?: string;
  value?: number;
}

interface AnalyticsConfig {
  enabled: boolean;
  trackingId?: string;
  debug?: boolean;
}

class Analytics {
  private config: AnalyticsConfig = {
    enabled: false,
    debug: false,
  };

  initialize(trackingId?: string, debug = false) {
    this.config.enabled = !!trackingId;
    this.config.trackingId = trackingId;
    this.config.debug = debug;

    if (this.config.enabled && trackingId) {
      // Initialize Google Analytics
      this.loadGoogleAnalytics(trackingId);
    }
  }

  private loadGoogleAnalytics(trackingId: string) {
    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(args);
    }
    gtag('js', new Date());
    gtag('config', trackingId);
    (window as any).gtag = gtag;
  }

  track(event: AnalyticsEvent) {
    if (this.config.debug) {
    }

    if (!this.config.enabled) return;

    // Send to Google Analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
    }
  }

  // Convenience methods
  trackPageView(path: string) {
    this.track({
      category: 'Navigation',
      action: 'page_view',
      label: path,
    });
  }

  trackToolUsed(toolName: string) {
    this.track({
      category: 'Tools',
      action: 'tool_used',
      label: toolName,
    });
  }

  trackDesignSaved(designId: string) {
    this.track({
      category: 'Design',
      action: 'design_saved',
      label: designId,
    });
  }

  trackDesignExported(format: string) {
    this.track({
      category: 'Design',
      action: 'design_exported',
      label: format,
    });
  }

  trackAddToCart(productId: string, quantity: number) {
    this.track({
      category: 'E-commerce',
      action: 'add_to_cart',
      label: productId,
      value: quantity,
    });
  }

  trackShareDesign(method: string) {
    this.track({
      category: 'Social',
      action: 'design_shared',
      label: method,
    });
  }

  trackError(errorMessage: string) {
    this.track({
      category: 'Errors',
      action: 'error_occurred',
      label: errorMessage,
    });
  }

  trackFunnelStep(step: number, stepName: string) {
    this.track({
      category: 'Funnel',
      action: 'step_reached',
      label: stepName,
      value: step,
    });
  }

  trackPerformance(metric: string, value: number) {
    this.track({
      category: 'Performance',
      action: metric,
      value: Math.round(value),
    });
  }

  // Backend analytics tracking for daily reports
  async trackOpenToBackend(productId: string, variantId?: string, productName?: string) {
    try {
      await fetch('/api/analytics/track-open', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          variantId,
          productName: productName || '',
          referrer: document.referrer,
          userAgent: navigator.userAgent,
        }),
      });
    } catch {
      // Silently fail - don't interrupt user experience
    }
  }

  async trackAddToCartToBackend(
    productId: string,
    variantId?: string,
    productName?: string,
    designId?: string,
    quantity = 1,
    cartId?: string
  ) {
    try {
      await fetch('/api/analytics/track-add-to-cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          variantId,
          productName: productName || '',
          designId,
          quantity,
          cartId,
        }),
      });
    } catch {
      // Silently fail - don't interrupt user experience
    }
  }
}

export const analytics = new Analytics();
export default analytics;
